import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@geely/ui";
import { Pencil, Trash2, Calendar } from 'lucide-react';
import type { WeeklyOperatingHours, Store } from '@geely/shared/types';
import { DAY_OF_WEEK_LABELS } from '@geely/shared/constants';

interface WeeklyHoursTableProps {
  hours: WeeklyOperatingHours[];
  userStore: Store | null;
  onEdit: (hours: WeeklyOperatingHours) => void;
  onDelete: (id: string) => void;
}

export function WeeklyHoursTable({ hours, userStore, onEdit, onDelete }: WeeklyHoursTableProps) {
  // Sort by day of week (0-6)
  const sortedHours = [...hours].sort((a, b) => a.dayOfWeek - b.dayOfWeek);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Horários Semanais</CardTitle>
      </CardHeader>
      <CardContent>
        {hours.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="size-12 mx-auto mb-3 text-gray-400" />
            <p>Nenhum horário semanal cadastrado.</p>
            <p className="text-sm mt-1">Configure os horários de funcionamento para cada dia da semana.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dia da Semana</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Horário</TableHead>
                <TableHead>Intervalo Almoço</TableHead>
                <TableHead>Intervalo</TableHead>
                <TableHead>Atend. Simultâneos</TableHead>
                <TableHead>Ativo</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedHours.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="font-medium">
                    {DAY_OF_WEEK_LABELS[h.dayOfWeek]}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                        Fechado
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        Aberto
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : (
                      <span>{h.startTime} - {h.endTime}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : h.hasLunchBreak ? (
                      <span className="text-sm">
                        {h.lunchStartTime} - {h.lunchEndTime}
                      </span>
                    ) : (
                      <span className="text-gray-500">Não</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : (
                      <span>{h.timeInterval} min</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : h.parallelAppointments !== undefined && h.parallelAppointments !== null ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        {h.parallelAppointments} (específico)
                      </Badge>
                    ) : (
                      <span className="text-gray-500">{userStore?.parallelAppointments} (padrão)</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isActive ? (
                      <Badge className="bg-green-100 text-green-800 border-green-300">Sim</Badge>
                    ) : (
                      <Badge variant="outline">Não</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(h)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(h.id)}
                      >
                        <Trash2 className="size-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
